/**
 * Request-access screen. Submits a PENDING account; the backend always answers
 * the same "submitted for approval" regardless of whether the email is new, so
 * this page never learns which addresses already exist.
 */
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Link, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AuthLayout } from "./AuthLayout";
import { api, ApiError } from "../lib/api";

const MIN_PASSWORD = 8;

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatch || tooShort) return;
    setError(null);
    setBusy(true);
    try {
      await api.register(email, fullName, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("auth.genericError"));
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("auth.requestSentTitle")}
        </Typography>
        <Alert severity="success">{t("auth.requestSentBody")}</Alert>
        <Button variant="contained" size="large" onClick={() => navigate("/login")}>
          {t("auth.backToLogin")}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("auth.registerTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("auth.haveAccount")}{" "}
          <Link component={RouterLink} to="/login" fontWeight={600}>
            {t("auth.signIn")}
          </Link>
        </Typography>
      </Box>

      <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label={t("auth.fullName")}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          required
          autoFocus
          fullWidth
        />
        <TextField
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          fullWidth
        />
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            label={t("auth.password")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            error={tooShort}
            helperText={tooShort ? t("auth.passwordTooShort", { min: MIN_PASSWORD }) : " "}
          />
          <TextField
            label={t("auth.confirmPassword")}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            error={mismatch}
            helperText={mismatch ? t("auth.passwordMismatch") : " "}
          />
        </Box>

        <Alert severity="info" icon={<InfoOutlinedIcon />}>
          {t("auth.approvalInfo")}
        </Alert>

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={busy || !fullName || !email || !password || mismatch || tooShort}
        >
          {busy ? t("auth.submitting") : t("auth.submitRequest")}
        </Button>
      </Box>
    </AuthLayout>
  );
}
